import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Flex } from '@chakra-ui/react';
import {
  ChakraAutocompleteField,
  ChakraFormSwitch,
  ChakraTextareaField,
  DateField,
  InputField,
} from 'app/components/Core';
import { useAppSelector } from 'app/store';
import { TimeSignatureInput } from 'app/components/Song/Details/TimeSignatureInput';
import { getImportRecordLink } from 'app/store/SongList/selectors';
import { useQueryParams } from 'app/components/SignUp/SignUpForm/hooks';
import { SongGenre } from './SongGenre';
import { isClearable } from './utils';
import { DurationTextField } from './DurationTextField';
import { SongDetailsFormProps } from './types';
import { SongTabFormWrapper } from '../../SongTabFormWrapper';
import { useSongDetailsForm, useSongDetailsFormData } from './hooks';
import { SongDetailsFormGrid } from '../SongDetailsFormGrid';
import { SongImportDetails } from './SongImportDetails';

export const SongDetailsForm: React.FC<SongDetailsFormProps> = (props) => {
  const { song, validations } = props;
  const importRecordLink = useAppSelector(getImportRecordLink);

  const {
    languages,
    genres,
    keySignatures,
    selectedGenres,
  } = useSongDetailsFormData(props);

  const {
    control,
    formState,
    register,
    onSubmit,
    setFocus,
  } = useSongDetailsForm(props);
  const { errors } = formState;

  const queryParams = useQueryParams();
  const focus = queryParams.get('focus');
  useEffect(() => {
    if (focus) {
      switch (focus) {
        case 'song-genre':
          document.getElementById(focus)?.focus();
          break;
        default:
          setFocus(focus as any);
      }
    }
  }, [setFocus, focus]);

  return (
    <SongTabFormWrapper onSubmit={onSubmit}>
      <SongDetailsFormGrid
        altTitle={
          <InputField
            label="Alt. Title"
            data-testid="Alt. Title"
            {...register('altTitle')}
            isInvalid={Boolean(errors.altTitle)}
            errorMessage={errors.altTitle?.message}
            onBlur={onSubmit}
          />
        }
        notes={
          <ChakraTextareaField
            data-testid="Notes"
            label="Notes"
            isInvalid={Boolean(errors.notes)}
            errorMessage={errors.notes?.message}
            width="100%"
            height="100%"
            formControlStyleProps={{ height: '100%' }}
            inputGroupProps={{ height: '100%' }}
            resize="none"
            wordBreak="break-word"
            bg="M.20"
            {...register('notes')}
            onBlur={onSubmit}
          />
        }
        artist={
          <InputField
            label="Artist"
            data-testid="Artist"
            {...register('displayArtistName')}
            isInvalid={Boolean(errors.displayArtistName)}
            errorMessage={errors.displayArtistName?.message}
            onBlur={onSubmit}
          />
        }
        genres={
          <Flex
            direction="column"
            height="full"
            alignItems="flex-start"
            justifyContent="center"
          >
            <SongGenre genreOptions={genres} selected={selectedGenres} />
          </Flex>
        }
        keySignature={
          <ChakraAutocompleteField
            control={control}
            isInvalid={Boolean(errors?.keySignature)}
            errorMessage={errors?.keySignature?.message}
            name="keySignature"
            label="Key Signature"
            dataTestId="Key Signature"
            options={keySignatures}
            isClearable={isClearable(validations.keySignature.enum)}
            onBlur={onSubmit}
          />
        }
        timeSignature={
          <TimeSignatureInput
            control={control}
            name="timeSignature"
            label="Time Signature"
            onChange={onSubmit}
          />
        }
        tempo={
          <InputField
            label="Tempo"
            {...register('tempo')}
            data-testid="Tempo"
            isInvalid={Boolean(errors.tempo)}
            errorMessage={errors.tempo?.message}
            onBlur={onSubmit}
          />
        }
        language={
          <ChakraAutocompleteField
            control={control}
            isInvalid={Boolean(errors?.language)}
            errorMessage={errors?.language?.message}
            name="language"
            label="Language"
            dataTestId="Language"
            options={languages}
            isClearable={isClearable(validations.language.enum)}
            onBlur={onSubmit}
          />
        }
        explicit={
          <ChakraFormSwitch
            control={control}
            label="Explicit Lyrics"
            name="explicit"
            onChange={onSubmit}
          />
        }
        duration={
          <Controller
            name="duration"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DurationTextField
                value={value}
                onChange={onChange}
                error={error?.message}
                onBlur={onSubmit}
              />
            )}
          />
        }
        creationDate={
          <DateField
            control={control}
            name="creationDate"
            label="Creation Date"
            onChange={() => {
              onSubmit();
            }}
            isRequired={Boolean(song.creationDate?.date)}
            dataTestId="Creation Date"
            shouldSaveInvalidDate
          />
        }
        location={
          <InputField
            label="Studio Location (main)"
            {...register('location.address')}
            data-testid="Studio Location (main)"
            isInvalid={Boolean(errors.location?.address)}
            errorMessage={errors.location?.address?.message}
            onBlur={onSubmit}
          />
        }
      />
      {importRecordLink && <SongImportDetails />}
    </SongTabFormWrapper>
  );
};
